/**
 * <s-size-quiz> — Vélocité paddle size finder (ported from the live store's size guide).
 *
 * Three radio questions (shoulders, hand, experience) recommend XS or M:
 *   - shoulder sensitivity → always XS
 *   - otherwise a smaller hand OR first-time paddle user → XS, else M
 *
 * Required markup:
 *   form[data-quiz-form]                 — the questions; submit shows the result
 *   [data-quiz-error]                    — shown when a question is unanswered
 *   [data-quiz-result]                   — result panel (hidden until submit)
 *   [data-quiz-size]                     — receives the recommended size label
 *   [data-quiz-note]                     — receives the explanation
 *   [data-quiz-shoulder]                 — extra note shown for shoulder sensitivity
 *   button[data-quiz-reset]              — start again
 *   button[data-quiz-apply]              — selects the recommended size on the product form
 *   template[data-quiz-copy]             — data-* attributes hold the translated labels and notes
 *
 * Attributes:
 *   data-product-form — id of the product form whose size radio should be selected
 */
class SSizeQuiz extends HTMLElement {
  #ac;
  #size = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('submit', (event) => {
      if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-quiz-form]')) return;
      event.preventDefault();
      this.#recommend(event.target);
    }, opts);
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-quiz-reset]')) this.#reset();
      if (event.target.closest('[data-quiz-apply]')) this.#apply();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #recommend(form) {
    const data = new FormData(form);
    const shoulders = data.get('shoulders');
    const hand = data.get('hand');
    const experience = data.get('experience');
    const error = this.querySelector('[data-quiz-error]');

    if (!shoulders || !hand || !experience) {
      if (error) error.hidden = false;
      return;
    }
    if (error) error.hidden = true;

    const copy = this.querySelector('[data-quiz-copy]')?.dataset || {};
    const shoulderPain = shoulders === 'yes';
    let note;
    if (shoulderPain) {
      this.#size = 'XS';
      note = copy.noteShoulder;
    } else if (hand === 'small' || experience === 'new') {
      this.#size = 'XS';
      note = copy.noteXs;
    } else {
      this.#size = 'M';
      note = copy.noteM;
    }

    this.querySelector('[data-quiz-size]').textContent = this.#size === 'XS' ? copy.sizeXs : copy.sizeM;
    this.querySelector('[data-quiz-note]').textContent = note || '';
    this.querySelector('[data-quiz-shoulder]').hidden = !shoulderPain;
    const apply = this.querySelector('[data-quiz-apply]');
    if (apply) apply.textContent = (copy.apply || '').replace('{size}', this.#size);

    form.hidden = true;
    const result = this.querySelector('[data-quiz-result]');
    result.hidden = false;
    result.querySelector('[data-quiz-size]')?.focus();
  }

  #reset() {
    const form = this.querySelector('[data-quiz-form]');
    form.reset();
    form.hidden = false;
    this.querySelector('[data-quiz-result]').hidden = true;
  }

  #apply() {
    const productForm = document.getElementById(this.dataset.productForm || '');
    // Prefer the chosen offer card (bundle or paddles) when the bundle-first picker is present.
    const scope = productForm?.querySelector("[data-offer-radio]:checked")?.closest("[data-offer]") || productForm;
    const radio = scope?.querySelector(`input[type="radio"][value="${this.#size}"]`) || productForm?.querySelector(`input[type="radio"][value="${this.#size}"]`);
    if (radio instanceof HTMLInputElement && !radio.disabled && !radio.checked) radio.click();
    this.closest('dialog')?.close();
  }
}

if (!customElements.get('s-size-quiz')) {
  customElements.define('s-size-quiz', SSizeQuiz);
}
